import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { AppShell } from '@/components/app/AppShell';
import {
  useComplianceItems,
  useComplianceSummary,
  useComplianceScan,
  useResolveItem,
  useWaiveItem,
  complianceLabel,
  type ComplianceItem,
} from '@/lib/compliance';
import { useEmployees } from '@/lib/employees';
import { useDraftFiling, useQueueDraft, useDismissDraft, type ComplianceDraft } from '@/lib/compliance-copilot';
import { MarkdownView } from '@/components/app/MarkdownView';
import { Sparkles } from 'lucide-react';
import { useState } from 'react';

const COUNTRY_FLAG: Record<string, string> = {
  JP: '🇯🇵', US: '🇺🇸', GB: '🇬🇧', DE: '🇩🇪', FR: '🇫🇷', ES: '🇪🇸',
  KR: '🇰🇷', CN: '🇨🇳', SG: '🇸🇬', IN: '🇮🇳', CA: '🇨🇦', AU: '🇦🇺',
};

export function CompliancePage() {
  const { t, i18n } = useTranslation();
  const [statusFilter, setStatusFilter] = useState<'open' | 'overdue' | 'done' | 'all'>('open');
  const [countryFilter, setCountryFilter] = useState<string>('');
  const items = useComplianceItems({ status: statusFilter, country: countryFilter || undefined });
  const summary = useComplianceSummary();
  const employees = useEmployees();
  const scan = useComplianceScan();
  const resolve = useResolveItem();
  const waive = useWaiveItem();
  const draftFiling = useDraftFiling();
  const [activeDraft, setActiveDraft] = useState<ComplianceDraft | null>(null);
  const [draftErr, setDraftErr] = useState<string | null>(null);
  const [draftingId, setDraftingId] = useState<string | null>(null);

  async function onDraft(itemId: string) {
    setDraftErr(null);
    setDraftingId(itemId);
    try {
      const d = await draftFiling.mutateAsync(itemId);
      setActiveDraft(d);
    } catch (e: unknown) {
      setDraftErr(((e as { response?: { data?: { message?: string } } })?.response?.data?.message) || (e as Error).message);
    } finally {
      setDraftingId(null);
    }
  }

  return (
    <AppShell>
      <div className="px-8 py-8 max-w-6xl">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="display text-3xl font-semibold tracking-tight text-ink-900 serif-jp">{t('compliance.title', { defaultValue: 'Compliance' })}</h1>
            <p className="mt-1 text-sm text-ink-500">
              {t('compliance.subtitle', { defaultValue: 'Always-on dashboard. The engine knows JP 社会保険・雇用保険・年末調整・給与支払報告書, US I-9/W-4, UK Right to Work, DE Sozialversicherung.' })}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/app/my-number"
              className="inline-flex items-center px-4 py-2 rounded-lg border border-accent-500/30 bg-accent-50 text-accent-700 text-sm font-semibold hover:bg-accent-100 transition"
            >
              🔐 My Number
            </Link>
            <button
              onClick={() => scan.mutate()}
              disabled={scan.isPending}
              className="inline-flex items-center px-4 py-2 rounded-lg border border-ink-300 text-ink-900 text-sm font-semibold hover:bg-ink-100 transition disabled:opacity-50"
            >
              {scan.isPending ? t('compliance.scanning', { defaultValue: 'Scanning…' }) : t('compliance.rescan', { defaultValue: '↻ Re-scan now' })}
            </button>
          </div>
        </div>

        <section className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
          <SummaryStat label={t('compliance.open', { defaultValue: 'Open' })} value={summary.data?.open ?? 0} className="text-ink-900" />
          <SummaryStat label={t('compliance.overdue', { defaultValue: 'Overdue' })} value={summary.data?.overdue ?? 0} className="text-warn-600" />
          <SummaryStat label={t('compliance.inProgress', { defaultValue: 'In progress' })} value={summary.data?.in_progress ?? 0} className="text-ink-700" />
          <SummaryStat label={t('compliance.resolved30d', { defaultValue: 'Resolved (30d)' })} value={summary.data?.done_30d ?? 0} className="text-ok-600" />
        </section>

        <div className="mt-6 flex flex-wrap items-center gap-2">
          {(['open','overdue','done','all'] as const).map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold capitalize transition ${statusFilter === s ? 'bg-accent-500 text-white' : 'bg-white text-ink-700 border border-ink-200 hover:bg-ink-50'}`}
            >
              {s}
            </button>
          ))}
          <select
            value={countryFilter}
            onChange={(e) => setCountryFilter(e.target.value)}
            className="ml-auto px-3 py-1.5 rounded-md text-xs font-semibold border border-ink-200 bg-white text-ink-700 hover:bg-ink-50 transition"
            aria-label={t('compliance.countryFilter', { defaultValue: 'Filter by country' })}
          >
            <option value="">{t('compliance.allCountries', { defaultValue: 'All countries' })}</option>
            {Object.entries(COUNTRY_FLAG).map(([code, flag]) => (
              <option key={code} value={code}>{flag} {code}</option>
            ))}
          </select>
        </div>

        <section className="mt-4 rounded-2xl border border-ink-200 bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-ink-50 text-ink-600">
                <tr>
                  <th className="text-start font-semibold px-4 py-2.5">{t('compliance.colTask', { defaultValue: 'Task' })}</th>
                  <th className="text-start font-semibold px-4 py-2.5">{t('compliance.colCountry', { defaultValue: 'Country' })}</th>
                  <th className="text-start font-semibold px-4 py-2.5">{t('compliance.colEmployee', { defaultValue: 'Employee' })}</th>
                  <th className="text-start font-semibold px-4 py-2.5">{t('compliance.colDue', { defaultValue: 'Due' })}</th>
                  <th className="text-start font-semibold px-4 py-2.5">{t('compliance.colStatus', { defaultValue: 'Status' })}</th>
                  <th className="text-start font-semibold px-4 py-2.5">{t('compliance.colGoTo', { defaultValue: 'Go to' })}</th>
                  <th className="text-end font-semibold px-4 py-2.5">{t('compliance.colAction', { defaultValue: 'Action' })}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {items.isLoading && <tr><td colSpan={7} className="text-center text-ink-500 py-8">{t('compliance.loading', { defaultValue: 'Loading…' })}</td></tr>}
                {items.data?.length === 0 && <tr><td colSpan={7} className="text-center text-ink-500 py-8">{t('compliance.allClear', { defaultValue: 'All clear in this view.' })}</td></tr>}
                {(items.data ?? []).map((i) => (
                  <Row
                    key={i.id}
                    i={i}
                    employeeId={i.employeeId}
                    employeeName={i.employeeId ? employees.data?.find((e) => e.id === i.employeeId)?.legalNameLocal ?? i.employeeId.slice(0, 8) : null}
                    locale={i18n.language}
                    onResolve={() => resolve.mutate(i.id)}
                    onWaive={() => waive.mutate(i.id)}
                    onDraft={() => onDraft(i.id)}
                    drafting={draftingId === i.id}
                  />
                ))}
              </tbody>
            </table>
          </div>
          {draftErr && <p className="mt-2 text-xs text-warn-600">{draftErr}</p>}
        </section>
      </div>
      {activeDraft && <CopilotDraftModal draft={activeDraft} onClose={() => setActiveDraft(null)} />}
    </AppShell>
  );
}

/** AI-drafted filing review — queue it for human approval or dismiss. */
function CopilotDraftModal({ draft, onClose }: { draft: ComplianceDraft; onClose: () => void }) {
  const { t } = useTranslation();
  const queue = useQueueDraft();
  const dismiss = useDismissDraft();
  const [status, setStatus] = useState(draft.status);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function doQueue() {
    setErr(null);
    try { await queue.mutateAsync(draft.id); setStatus('queued'); setMsg(t('copilot.queued', { defaultValue: 'Queued for approval — routed to an owner/admin.' })); }
    catch (e: unknown) { setErr(((e as { response?: { data?: { message?: string } } })?.response?.data?.message) || (e as Error).message); }
  }
  async function doDismiss() {
    try { await dismiss.mutateAsync(draft.id); onClose(); } catch { /* noop */ }
  }

  return (
    <div className="fixed inset-0 z-50 bg-ink-950/40 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-glow border border-ink-200 max-w-2xl w-full p-6 max-h-[88vh] overflow-y-auto">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-accent-600" />
          <span className="text-[10px] font-semibold uppercase tracking-wide text-accent-700">{t('copilot.aiDraft', { defaultValue: 'AI-drafted filing' })}</span>
          {draft.model && <span className="ms-auto text-[10px] text-ink-400">{draft.model}</span>}
        </div>
        <h2 className="mt-2 text-lg font-semibold text-ink-900">{draft.title}</h2>
        {draft.rationale && (
          <div className="mt-2 rounded-lg border border-accent-500/20 bg-accent-50/60 px-3 py-2">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-accent-700">{t('copilot.why', { defaultValue: 'Why now' })}</p>
            <p className="text-sm text-ink-700">{draft.rationale}</p>
          </div>
        )}
        <div className="mt-3 rounded-lg border border-ink-200 bg-ink-50/40 p-3">
          <MarkdownView>{draft.bodyMd}</MarkdownView>
        </div>
        <p className="mt-2 text-[11px] text-ink-400">{t('copilot.disclaimer', { defaultValue: 'AI-generated — review every field. Filing is prepared, not transmitted; submit via e-Gov after approval.' })}</p>
        {err && <div className="mt-3 rounded-md bg-warn-50 border border-warn-500/30 text-warn-600 text-sm px-3 py-2">{err}</div>}
        {msg && <div className="mt-3 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm px-3 py-2">{msg}</div>}
        <div className="flex justify-end gap-2 pt-5">
          <button onClick={doDismiss} disabled={dismiss.isPending} className="px-3 py-2 text-sm font-semibold text-ink-600 hover:bg-ink-100 rounded-lg">{t('copilot.dismiss', { defaultValue: 'Dismiss' })}</button>
          <button onClick={doQueue} disabled={queue.isPending || status !== 'draft'} className="px-4 py-2 rounded-lg bg-accent-500 text-white text-sm font-semibold hover:bg-accent-600 disabled:opacity-50">
            {status === 'queued' ? t('copilot.queuedBtn', { defaultValue: 'Queued ✓' }) : queue.isPending ? t('copilot.queueing', { defaultValue: 'Queueing…' }) : t('copilot.queue', { defaultValue: 'Queue for approval' })}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Map a compliance item kind to the app route it belongs to, or null if none. */
function kindToRoute(kind: string): string | null {
  const k = kind.toLowerCase();
  if (k.includes('nencho') || k.includes('gensen') || k.includes('hotei') || k.includes('kyuyo_shiharai') || k.includes('chosho')) {
    return '/app/year-end';
  }
  if (k.includes('payroll')) {
    return '/app/payroll';
  }
  if (k.includes('insurance') || k.includes('shikaku')) {
    return '/app/people';
  }
  return null;
}

function Row({ i, employeeId, employeeName, locale, onResolve, onWaive, onDraft, drafting }: { i: ComplianceItem; employeeId: string | null; employeeName: string | null; locale: string; onResolve: () => void; onWaive: () => void; onDraft: () => void; drafting: boolean }) {
  const { t } = useTranslation();
  const today = new Date().toISOString().slice(0, 10);
  const isOverdue = i.dueDate != null && String(i.dueDate).slice(0, 10) < today && i.status !== 'done' && i.status !== 'waived';
  const actionRoute = kindToRoute(i.kind);
  return (
    <tr className={`hover:bg-ink-50/40 ${isOverdue ? 'bg-warn-50/30' : ''}`}>
      <td className="px-4 py-3 text-ink-900 font-medium">
        {complianceLabel(i.kind, locale)}
        {i.metadata?.source ? (
          <p className="text-[10px] text-ink-500 mt-0.5 font-normal">出典: {String(i.metadata.source)}</p>
        ) : null}
      </td>
      <td className="px-4 py-3 text-ink-700">
        {COUNTRY_FLAG[i.countryCode] ?? ''} {i.countryCode}
      </td>
      <td className="px-4 py-3 text-ink-700">
        {employeeId ? (
          <Link
            to={`/app/people/${employeeId}`}
            className="text-accent-600 hover:underline hover:text-accent-700 transition-colors"
          >
            {employeeName ?? employeeId.slice(0, 8)}
          </Link>
        ) : (
          <span className="text-ink-400">{t('compliance.company', { defaultValue: '— company —' })}</span>
        )}
      </td>
      <td className="px-4 py-3 num text-ink-700">
        {i.dueDate ? String(i.dueDate).slice(0, 10) : '—'}
      </td>
      <td className="px-4 py-3"><StatusPill status={i.status} overdue={isOverdue} /></td>
      <td className="px-4 py-3">
        {actionRoute ? (
          <Link
            to={actionRoute}
            className="px-2 py-1 rounded-md bg-ink-100 text-ink-600 text-xs font-semibold hover:bg-ink-200 transition whitespace-nowrap"
          >
            {t('compliance.goLink', { defaultValue: 'Go →' })}
          </Link>
        ) : (
          <span className="text-ink-300 text-xs">—</span>
        )}
      </td>
      <td className="px-4 py-3 text-end">
        {(i.status === 'open' || i.status === 'in_progress' || i.status === 'overdue') ? (
          <div className="flex items-center justify-end gap-1.5">
            <button onClick={onDraft} disabled={drafting} title={t('compliance.draftWithAi', { defaultValue: 'Draft the filing with AI' })} className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-accent-50 text-accent-700 text-xs font-semibold hover:bg-accent-100 transition disabled:opacity-50">
              <Sparkles className="h-3 w-3" />{drafting ? '…' : t('compliance.draft', { defaultValue: 'Draft' })}
            </button>
            <button onClick={onResolve} className="px-2 py-1 rounded-md bg-ok-50 text-ok-600 text-xs font-semibold hover:bg-ok-500 hover:text-white transition">{t('compliance.resolve', { defaultValue: 'Resolve' })}</button>
            <button onClick={onWaive} className="px-2 py-1 rounded-md bg-ink-100 text-ink-600 text-xs font-semibold hover:bg-ink-300 transition">{t('compliance.waive', { defaultValue: 'Waive' })}</button>
          </div>
        ) : (
          <span className="text-[10px] text-ink-400">{i.status}</span>
        )}
      </td>
    </tr>
  );
}

function StatusPill({ status, overdue }: { status: ComplianceItem['status']; overdue: boolean }) {
  if (overdue) return <span className="inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded-md bg-warn-500 text-white">● Overdue</span>;
  const map: Record<ComplianceItem['status'], { cls: string; label: string }> = {
    open:        { cls: 'bg-ink-100 text-ink-700', label: '○ Open' },
    in_progress: { cls: 'bg-accent-50 text-accent-700', label: '◐ In progress' },
    done:        { cls: 'bg-ok-50 text-ok-600',    label: '● Done' },
    overdue:     { cls: 'bg-warn-50 text-warn-600',label: '● Overdue' },
    waived:      { cls: 'bg-ink-100 text-ink-400 line-through', label: '— Waived' },
  };
  const m = map[status];
  return <span className={`inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded-md ${m.cls}`}>{m.label}</span>;
}

function SummaryStat({ label, value, className }: { label: string; value: number; className?: string }) {
  return (
    <div className="rounded-2xl border border-ink-200 bg-white p-4">
      <p className="text-[10px] uppercase tracking-wide text-ink-500 font-semibold">{label}</p>
      <p className={`num mt-1 text-2xl font-bold ${className ?? 'text-ink-900'}`}>{value}</p>
    </div>
  );
}
